import { useEffect, useState } from 'react'
import { SiteNav } from '../components/SiteNav'
import { FeedbackModal } from '../components/FeedbackModal'
import { setPageMeta } from '../utils'

export function AboutPage() {
  const [showFeedback, setShowFeedback] = useState(false)

  useEffect(() => {
    setPageMeta({
      title: 'About | arisk.ai',
      description: 'arisk.ai is a privacy tool for professionals who need to use AI without exposing sensitive data. Built by a technologist who faced the same problem.',
      canonical: 'https://arisk.ai/about',
    })
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 text-gray-800 dark:text-neutral-200">
      <SiteNav />

      <div className="max-w-2xl mx-auto px-6 py-12">

        {/* Hero + Founder */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">About arisk.ai</h1>

          {/* Founder card */}
          <div className="p-5 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Naseer Khan</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">Founder, arisk.ai</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">Technologist · Data &amp; AI platforms · Quantitative finance</div>
              </div>
              <a
                href="https://www.linkedin.com/in/nkai"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="Naseer Khan on LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Origin story */}
          <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">
            I built arisk.ai to solve a problem I faced directly. Even while building production AI
            systems at wealth management institutions, where data classification and audit requirements
            are non-negotiable, reviewing legal documents and counterparty agreements meant commercial
            AI tools were completely off the table. Colleagues in legal, compliance, and healthcare
            started asking to use it. I made it public and have been developing it since,
            based on feedback from people using it in real workflows.
          </p>
        </div>

        <div className="space-y-12 text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">

          {/* The problem */}
          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">The problem</h2>
            <p className="mb-4">
              AI tools have become genuinely useful for document work. Summarising contracts, extracting
              key terms, reviewing clauses, drafting responses: these tasks take minutes with a capable
              language model and hours without one.
            </p>
            <p>
              For most professionals, the documents that would benefit most from AI assistance are also
              the ones that cannot be shared with a third party. A contract contains counterparty data.
              A client file contains personal information. The moment you paste any of that into ChatGPT
              or Claude, it leaves your device and enters infrastructure you do not control. Enterprise
              plans help at the margins. Privacy policies change. Legal requests can compel disclosure.
              For professionals with real confidentiality obligations, none of that is acceptable.
            </p>
          </section>

          {/* The approach */}
          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">The approach</h2>
            <p className="mb-4">
              The only reliable way to protect sensitive information when using an AI tool is to ensure
              it never reaches the model in its original form. arisk.ai runs entirely in your browser.
              Nothing is sent to a server. No account is required. The token mapping exists only in your session.
            </p>
            <p className="mb-4">
              It works by replacing sensitive words with meaningless tokens before you copy your text.
              The AI works with a structurally identical document in which the identifiers have been
              swapped for placeholders. The sensitive values never reach the model.
            </p>
            <p>
              This works because language models do not need to know that a counterparty is called Acme
              Corp or that an account number is 7842910. They need the structure, the grammar, and the
              context. Tokens preserve all of that. When the AI responds, you paste its output back into
              arisk.ai and restore the originals.
            </p>
          </section>

          {/* Who it is for */}
          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Who it is for</h2>
            <p className="mb-5">
              Built for professionals who face a real tension between using AI productively and meeting
              their confidentiality obligations.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Legal</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Solicitors, barristers, and in-house counsel working with privileged communications and client files.</div>
              </div>
              <div className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Finance</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Analysts, advisers, and compliance professionals handling client data and non-public financial information.</div>
              </div>
              <div className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Healthcare</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Clinicians and administrators working with patient records, referrals, and clinical correspondence.</div>
              </div>
              <div className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Compliance and risk</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Teams reviewing sensitive internal documents where regulatory constraints limit what can be shared externally.</div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section>
            <div className="border-t border-gray-200 dark:border-neutral-700 pt-10 text-center">
              <p className="text-sm text-gray-500 dark:text-neutral-400 mb-5">
                Questions, feedback, or ideas about arisk.ai?
              </p>
              <button
                onClick={() => setShowFeedback(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Get in touch
              </button>
              <p className="mt-4 text-xs text-gray-400 dark:text-neutral-500">
                Or email{' '}
                <a
                  href="mailto:hello@arisk.ai"
                  className="hover:text-gray-600 dark:hover:text-neutral-300 underline decoration-dotted underline-offset-2"
                >
                  hello@arisk.ai
                </a>
              </p>
            </div>
          </section>

        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 pb-8 text-right">
        <span className="font-mono text-xs text-gray-300 dark:text-neutral-700">v{__APP_VERSION__} · {__GIT_SHA__}</span>
      </div>

      <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  )
}
